import { useEffect, type RefObject } from 'react';

/**
 * ارتفاعُ الجدول ذي الرأس الثابت مقيسًا لا مقدَّرًا.
 *
 * الجدول يُمرَّر داخل غلافه ورأسُه يلتصق بأعلى الغلاف. فإن كان الغلاف أطول
 * مما يتّسع تحت شريط الأدوات الملتصق، مرّر المستخدمُ الصفحةَ حتى آخرها فصعد
 * الغلاف تحت الشريط واختفى رأسُ الجدول خلفه — وهو ما كان: «مئةٌ ناقص مئتين»
 * تقديرٌ لما فوق الجدول وتحته، فلمّا تغيّر ما تحته (الترقيم، الرقاقات، شريط
 * التحديد) غطّى الشريطُ الرأسَ بخمسة وثلاثين بكسلًا.
 *
 * فيُقاس: ارتفاعُ ما يُعرض من الصفحة، ناقصَ الشريط الملتصق، ناقصَ ما بعد
 * الجدول حتى آخر الصفحة. فإذا مُرّرت الصفحة إلى آخرها جاء الجدول تحت الشريط
 * تمامًا، ورأسُه أوّلُ ما تحته. ويُكتب في `--fit-h` والقاعدة في CSS تقرؤه.
 */
const SELECTOR = '.table-wrap.sticky-head:not(.sticky-head-flow):not(.rows-cap):not(.table-big)';
const MIN = 240;

function fit(main: HTMLElement): void {
  const mainRect = main.getBoundingClientRect();
  for (const wrap of main.querySelectorAll<HTMLElement>(SELECTOR)) {
    const page = wrap.closest('.page');
    const bar = page?.querySelector<HTMLElement>('.sticky-bar');
    const barH = bar ? bar.offsetHeight : 0;
    /* ما بعد الغلاف حتى آخر محتوى الصفحة، في إحداثيّات التمرير. */
    const wrapBottom = wrap.getBoundingClientRect().bottom - mainRect.top + main.scrollTop;
    const after = Math.max(0, main.scrollHeight - wrapBottom);
    const h = Math.max(MIN, Math.floor(main.clientHeight - barH - after));
    const next = `${h}px`;
    if (wrap.style.getPropertyValue('--fit-h') !== next) wrap.style.setProperty('--fit-h', next);
  }
}

export function useStickyTableFit(mainRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fit(main));
    };

    /* الصفحة تتبدّل تحت `main` بلا إشعار: تنقّلٌ، أو بيانات تصل، أو رقاقةٌ تظهر. */
    const mo = new MutationObserver(schedule);
    mo.observe(main, { childList: true, subtree: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(main);
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [mainRef]);
}
